import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import type { Lecture } from './lecture';

export function Bloc<T>({
  titre,
  icone,
  lecture,
  lien,
  children,
}: {
  titre: string;
  icone?: ReactNode;
  lecture: Lecture<T>;
  lien?: { to: string; libelle: string };
  children: (valeur: T) => ReactNode;
}) {
  return (
    <section className="flex flex-col rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
        {icone}
        {titre}
      </h2>
      <div className="flex-1">
        {lecture.etat === 'chargement' && (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Lecture…
          </p>
        )}
        {lecture.etat === 'erreur' && (
          <p className="flex items-start gap-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {lecture.message}
          </p>
        )}
        {lecture.etat === 'pret' && children(lecture.valeur)}
      </div>
      {lien && (
        <Link
          to={lien.to}
          className="mt-4 inline-flex items-center gap-1 self-start text-sm font-medium text-primary-700 hover:text-primary-900"
        >
          {lien.libelle} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </section>
  );
}

/** Un nombre, et ce qu'il compte. */
export function Chiffre({ valeur, libelle, ton = 'neutre' }: {
  valeur: ReactNode;
  libelle: string;
  ton?: 'neutre' | 'attention' | 'alerte';
}) {
  return (
    <div>
      <p className={
        ton === 'alerte' ? 'text-2xl font-bold text-red-700'
          : ton === 'attention' ? 'text-2xl font-bold text-amber-700'
            : 'text-2xl font-bold text-gray-900'
      }>
        {valeur}
      </p>
      <p className="text-xs text-gray-500">{libelle}</p>
    </div>
  );
}
